// ABOUTME: Prepares form files and e-mail templates before API submission.
// ABOUTME: Keeps job e-mail references isolated while preserving safe retries.

import { apiDirectCall } from "customHooks/useApi";
import { handleFileNameUpload } from "functions/util";
import _ from "lodash";
import { buildClientRequestFingerprint } from "../../../lib/clientRequestId";
import { isFileFromDB } from "../Inputs/File/File";
import {
    applyEmailReferenceWrite,
    EmailPersistenceMode,
    getEmailReferenceWrite,
    getPendingEmailCopyRequest
} from "./emailPersistence";

export const TempEmailFieldName = "_tempEmailData";

export type EmailMaterializationContext = {
    loadedEmailDataBySource: Map<string, Compleo.IObject | undefined>;
    materializedEmailIdByFingerprint: Map<string, string>;
};

export const createEmailMaterializationContext =
    (): EmailMaterializationContext => ({
        loadedEmailDataBySource: new Map(),
        materializedEmailIdByFingerprint: new Map()
    });

export const getFilesFields = (values: any) => {
    const keys = Object.keys(values);
    const fileFields = keys.filter((v: any) => {
        return v.includes("_filesDefinition");
    });

    // complex Fields
    for (const key of keys) {
        if (Array.isArray(values[key])) {
            for (let index = 0; index < values[key].length; index++) {
                if (typeof values[key][index] === "object") {
                    const insideKeys = Object.keys(values[key][index]);
                    const fileFieldsInside = insideKeys
                        .filter((v: string) => {
                            return v.includes("_filesDefinition");
                        })
                        .map((v: any) => `${key}[${index}].${v}`);
                    fileFields.push(...fileFieldsInside);
                }
            }
        }
    }

    return fileFields.map((f: any) => f.replace("_filesDefinition", ""));
};

export const processUploadFileFields = async (
    values: any,
    companyId: number = 0
) => {
    const fileFields = getFilesFields(values);

    if (fileFields.length > 0) {
        const re = /(?:\.([^.]+))?$/;
        const filesAndExtensions: Compleo.IObject[] = [];
        for (const fieldToUpload of fileFields) {
            // const fieldNameToUpload = `${fieldToUpload}_filesDefinition`;
            // const files = values[fieldNameToUpload].values;
            // const files = _.get(values, fieldNameToUpload).values;
            const files = _.get(values, fieldToUpload);
            const isMultiple = Array.isArray(files);
            const fileList = isMultiple ? files : [files];
            if (files && fileList.length) {
                for (const fileDef of fileList) {
                    const file = fileDef?.FileInfo || fileDef;
                    if (!isFileFromDB(file)) {
                        const extension = (re.exec(file.name) || [])[1];
                        filesAndExtensions.push({
                            extension: extension,
                            originalFileName: handleFileNameUpload(file.name),
                            fieldName: fieldToUpload,
                            contentType: file.type
                        });
                    }
                }
            }
        }
        if (filesAndExtensions.length) {
            const axPost = await apiDirectCall(
                "/general/requests3url",
                "post",
                {
                    files: filesAndExtensions,
                    companyId: values.companyId || companyId
                }
            );
            const urls = axPost.data;
            for (const fieldToUpload of fileFields) {
                const fieldNameToUpload = fieldToUpload;
                // const fieldNameToUploadDef = `${fieldToUpload}_filesDefinition`;
                const valueDefinition: Compleo.IObject[] = [];
                const files = _.get(values, fieldNameToUpload);
                const isMultiple = Array.isArray(files);
                const fileList = isMultiple ? files : [files];
                if (files && fileList.length && urls) {
                    for (const fileDef of fileList) {
                        const file = fileDef?.FileInfo;
                        if (!isFileFromDB(fileDef)) {
                            const returnData = urls.filter(
                                (u: any) =>
                                    u.originalFileName ===
                                        handleFileNameUpload(file.name) &&
                                    u.fieldName === fieldNameToUpload
                            )[0];
                            const signedUrl = returnData.uploadURL;
                            const filename = returnData.fileName;
                            const options = {
                                headers: {
                                    "Content-Type":
                                        returnData.contentType || file.type,
                                    ...(returnData.cacheControl
                                        ? {
                                              "Cache-Control":
                                                  returnData.cacheControl
                                          }
                                        : {}),
                                    "x-amz-tagging": `originalFileName=${handleFileNameUpload(
                                        file.name
                                    )}`
                                }
                            };
                            const axiosReturn = await apiDirectCall(
                                signedUrl,
                                "put",
                                file,
                                options,
                                true,
                                false
                            );
                            const itemToAdd: Compleo.IObject = {
                                key: filename,
                                originalFileName: file.name
                            };
                            if ((fileDef.tags || []).length) {
                                itemToAdd.tags = fileDef.tags;
                            }
                            valueDefinition.push(itemToAdd);
                        } else {
                            valueDefinition.push(fileDef);
                        }
                    }
                }
                if (isMultiple) {
                    _.set(values, fieldNameToUpload, valueDefinition);
                } else {
                    _.set(values, fieldNameToUpload, valueDefinition[0]);
                }
            }
        }
    }
    return values;
};

const jobEmailReferenceFields = new Set([
    "emailTemplate",
    "emailUserTemplate",
    "email"
]);

const hasTempEmailData = (fieldItem: any) =>
    fieldItem &&
    typeof fieldItem === "object" &&
    Object.prototype.hasOwnProperty.call(fieldItem, TempEmailFieldName);

const isUnloadedJobEmail = (
    fieldName: string,
    fieldItem: any,
    persistenceMode: EmailPersistenceMode
) =>
    !hasTempEmailData(fieldItem) &&
    jobEmailReferenceFields.has(fieldName) &&
    Boolean(fieldItem?.id) &&
    (persistenceMode === "job-create" ||
        (persistenceMode === "job-edit" && !fieldItem.jobSpecific));

export const getEmailFields = (
    values: any,
    persistenceMode: EmailPersistenceMode = "update"
) => {
    const fieldNames = Object.keys(values);
    const emailFields: string[] = [];
    for (const fieldName of fieldNames) {
        if (
            Array.isArray(values[fieldName]) &&
            values[fieldName].some(
                (fieldItem: any) =>
                    hasTempEmailData(fieldItem) ||
                    isUnloadedJobEmail(
                        fieldName,
                        fieldItem,
                        persistenceMode
                    )
            )
        ) {
            emailFields.push(fieldName);
        }
    }
    return emailFields;
};

export const processEmailFields = async (
    values: any,
    persistenceMode: EmailPersistenceMode = "update",
    materializationContext: EmailMaterializationContext =
        createEmailMaterializationContext()
) => {
    const emailFields = getEmailFields(values, persistenceMode);
    if (emailFields.length > 0) {
        for (const fieldName of emailFields) {
            const fieldData = values[fieldName];
            for (const fieldItem of fieldData) {
                if (
                    !hasTempEmailData(fieldItem) &&
                    !isUnloadedJobEmail(
                        fieldName,
                        fieldItem,
                        persistenceMode
                    )
                ) {
                    continue;
                }
                if (!hasTempEmailData(fieldItem)) {
                    const sourceKey = buildClientRequestFingerprint({
                        companyId: values.companyId,
                        sourceId: fieldItem.id
                    });
                    let emailData =
                        materializationContext.loadedEmailDataBySource.get(
                            sourceKey
                        );
                    if (
                        !materializationContext.loadedEmailDataBySource.has(
                            sourceKey
                        )
                    ) {
                        const dataReturn = await apiDirectCall(
                            "/email/getemail",
                            "post",
                            {
                                id: fieldItem.id,
                                companyId: values.companyId
                            }
                        );
                        emailData = dataReturn?.data?.data;
                        materializationContext.loadedEmailDataBySource.set(
                            sourceKey,
                            _.cloneDeep(emailData)
                        );
                    }
                    if (!emailData) {
                        continue;
                    }
                    fieldItem[TempEmailFieldName] = {
                        subject: emailData.subject || "",
                        body: emailData.body || "",
                        attachments: _.cloneDeep(emailData.attachments || []),
                        jobEmailSourceId: fieldItem.id
                    };
                }
                await processEmailAttachments(fieldItem, values.companyId);
                await processEmailData(
                    fieldItem,
                    values.companyId,
                    persistenceMode,
                    materializationContext
                );
            }
        }
    }

    // this block is just cloear tempEmailData before submit
    const clonedValues = _.cloneDeep(values);
    const emailFieldsToClear = getEmailFields(clonedValues);
    if (emailFieldsToClear.length > 0) {
        for (const fieldName of emailFieldsToClear) {
            const fieldData = clonedValues[fieldName];
            for (const fieldItem of fieldData) {
                if (hasTempEmailData(fieldItem)) {
                    delete fieldItem[TempEmailFieldName];
                }
            }
        }
    }
    return clonedValues;
};

const processEmailAttachments = async (fieldItem: any, companyId: number) => {
    const tempData = fieldItem[TempEmailFieldName];
    if ((tempData.attachments || []).length) {
        let tempDataCloned = _.cloneDeep(tempData);
        tempDataCloned = await processUploadFileFields(
            tempDataCloned,
            companyId
        );
        fieldItem[TempEmailFieldName] = tempDataCloned;
    }
};

const processEmailData = async (
    fieldItem: any,
    companyId: number,
    persistenceMode: EmailPersistenceMode,
    materializationContext: EmailMaterializationContext
) => {
    const tempData = fieldItem[TempEmailFieldName];

    // clear path from attachments
    const attachments: { key: string; originalFileName: string }[] = [];

    for (const attachment of tempData.attachments || []) {
        attachments.push({
            key: attachment.key,
            originalFileName: attachment.originalFileName
        });
    }

    const postObj: Compleo.IObject = {
        companyId: companyId,
        subject: tempData.subject,
        body: tempData.body
    };

    if (attachments.length) {
        postObj.attachments = attachments;
    }
    const pendingRequest = getPendingEmailCopyRequest(
        fieldItem,
        persistenceMode,
        postObj
    );
    if (pendingRequest?.sourceId && !tempData.jobEmailSourceId) {
        tempData.jobEmailSourceId = pendingRequest.sourceId;
    }
    if (pendingRequest) {
        const recoveryResponse = await apiDirectCall(
            "/email/putemail",
            "post",
            pendingRequest
        );
        applyEmailReferenceWrite(
            fieldItem,
            persistenceMode,
            recoveryResponse.data?.id
        );
    }
    const writeReference = getEmailReferenceWrite(
        fieldItem,
        persistenceMode,
        postObj
    );
    if (writeReference.sourceId && !tempData.jobEmailSourceId) {
        tempData.jobEmailSourceId = writeReference.sourceId;
    }
    Object.assign(postObj, writeReference);

    const materializationKey =
        persistenceMode === "update" || !tempData.jobEmailSourceId
            ? undefined
            : buildClientRequestFingerprint({
                  ...postObj,
                  id: undefined,
                  sourceId: tempData.jobEmailSourceId
              });
    const materializedEmailId = materializationKey
        ? materializationContext.materializedEmailIdByFingerprint.get(
              materializationKey
          )
        : undefined;
    if (materializedEmailId) {
        applyEmailReferenceWrite(
            fieldItem,
            persistenceMode,
            materializedEmailId
        );
        return;
    }

    const axPost = await apiDirectCall("/email/putemail", "post", postObj);
    const id = axPost.data?.id;
    applyEmailReferenceWrite(fieldItem, persistenceMode, id);
    if (materializationKey) {
        materializationContext.materializedEmailIdByFingerprint.set(
            materializationKey,
            id
        );
    }
};

// "fefcb3a5-14ab-4835-97ac-f47933261a6c"
// "0a255bd0-7cb4-4298-b094-a208be3c12b2"
